import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from '../config';
import { isE2EAdminUserMode, isE2ESupportUserMode, isE2ETestMode } from '../utils/e2e-test-mode';
import type { FiscalYear } from '../utils/fiscal-year';
import type { UserActivityDetail } from '../types/monitoring';
import type { UserEventPayload } from '../types/user-events';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: Record<string, unknown>;
  pagination?: {
    page: number;
    limit: number;
    total?: number;
    totalPages?: number;
  };
}

export interface AuthMePersona {
  email: string;
  isSupportUser: boolean;
  isAdmin: boolean;
  canAccessMonitoring: boolean;
  canAccessAnalytics: boolean;
}

export interface EhubLogEntry {
  timestamp: string;
  logGroup: string;
  message: string;
  isError: boolean;
}

export interface BcPermissionRecord {
  code: string;
  accessType: 'read' | 'write' | 'unknown';
  isWrite: boolean;
}

export interface BcStoreUserRecord {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  locale: string;
  status: string;
  lastLoginAt: string;
  updatedAt: string;
  storeHash: string;
  storeName: string;
  permissions: BcPermissionRecord[];
}

export interface BcStoreSummary {
  storeHash: string;
  storeName: string;
  status: 'ok' | 'error';
  userCount?: number;
  errorMessage?: string;
}

export interface BcStoreWarning {
  storeHash: string;
  storeName: string;
  message: string;
}

export interface BcPermissionsAuditResponse {
  storeHash: string;
  storeName: string;
  stores: BcStoreSummary[];
  storeWarnings?: BcStoreWarning[];
  users: BcStoreUserRecord[];
  fetchedAt: string;
  totalUsers: number;
  totalPermissions: number;
  writePermissionCount: number;
}

export interface PermissionTemplateSummary {
  id: string;
  name: string;
  description: string;
  permissionCount: number;
}

export interface PermissionTemplate {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface UserTemplateComparison {
  email: string;
  status: string;
  match: boolean;
  missing: string[];
  extra: string[];
}

export interface PermissionTemplateCompareResult {
  templateId: string;
  templateName: string;
  totalUsers: number;
  matchingUsers: number;
  users: UserTemplateComparison[];
}

class ApiClient {
  private _client: AxiosInstance | null = null;
  private getAccessToken: (() => Promise<string | null>) | null = null;
  private getAccountEmail: (() => string | null) | null = null;
  private onAuthError: (() => void) | null = null;

  private get client(): AxiosInstance {
    if (!this._client) {
      const apiBaseUrl = getApiBaseUrl();
      this._client = axios.create({
        baseURL: apiBaseUrl,
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      });

      this._client.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
          const e2eTestMode = isE2ETestMode();
          const isLocalApi = apiBaseUrl.includes('localhost') || apiBaseUrl.includes('127.0.0.1');
          const e2eEmail =
            (typeof window !== 'undefined' && window.localStorage.getItem('E2E_USER_EMAIL'))
            || this.getAccountEmail?.()
            || null;

          if (!e2eTestMode && this.getAccessToken && config.url?.startsWith('/api')) {
            const token = await this.getAccessToken();
            if (token) { config.headers.Authorization = `Bearer ${token}`; }
          }
          if (isE2EAdminUserMode()) {
            config.headers['X-E2E-Admin-User'] = 'true';
          } else if (isE2ESupportUserMode()) {
            config.headers['X-E2E-Support-User'] = 'true';
          } else if ((e2eTestMode || isLocalApi) && e2eEmail) {
            config.headers['X-E2E-User-Email'] = e2eEmail;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      this._client.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error.response) {
            const status = error.response.status;
            // 403 is authorization (forbidden), not authentication — do not log the user out.
            if (status === 401 && this.onAuthError) {
              this.onAuthError();
            }
            return Promise.reject({
              message: error.response.data?.message || error.response.data?.error || 'An error occurred',
              status,
              data: error.response.data,
            });
          } else if (error.request) {
            return Promise.reject({ message: 'No response from server', status: 0 });
          } else {
            return Promise.reject({ message: error.message || 'An unknown error occurred', status: 0 });
          }
        }
      );
    }
    return this._client;
  }

  setAccessTokenGetter(getter: () => Promise<string | null>) {
    this.getAccessToken = getter;
  }

  setAccountEmailGetter(getter: () => string | null) {
    this.getAccountEmail = getter;
  }

  setAuthErrorHandler(handler: () => void) {
    this.onAuthError = handler;
  }

  /** Make a GET request. Environment is determined server-side by TARGET_ENV. */
  private async get<T>(
    endpoint: string,
    params?: Record<string, any>
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get(endpoint, { params });
      return response.data;
    } catch (error: any) {
      console.error('API Request failed:', { endpoint, error: error.message, status: error.status });
      throw error;
    }
  }

  // Orders API
  async getOrders(page: number = 1, limit: number = 50, status: string = 'all') {
    const params: Record<string, string | number> = { page, limit };
    if (status && status !== 'all') {
      params.status = status;
    }
    return this.get<any[]>('/api/orders', params);
  }

  async searchOrdersByEmail(email: string, limit: number = 250) {
    return this.get<any[]>('/api/orders', { email: email.trim(), limit });
  }

  async getOrderById(orderId: number) {
    return this.get<any>(`/api/orders/${orderId}`);
  }

  async getOrderTimeline(orderId: number) {
    return this.get<any>(`/api/orders/${orderId}/timeline`);
  }

  async getOrderEhubLogs(
    orderId: number,
    orderCreatedAt: string
  ): Promise<ApiResponse<{ results: EhubLogEntry[] }>> {
    return this.get<{ results: EhubLogEntry[] }>(
      `/api/orders/${orderId}/ehub-logs`,
      { createdAt: orderCreatedAt }
    );
  }

  // Promotions API
  async getPromotions(
    page: number = 1,
    limit: number = 50,
    type: 'coupons' | 'automatic' = 'coupons',
    status: 'all' | 'active' | 'inactive' = 'all',
    search?: string,
  ) {
    const params: Record<string, string | number> = { page, limit, type, status };
    const normalizedSearch = search?.trim();
    if (normalizedSearch) {
      params.search = normalizedSearch;
    }
    return this.get<any[]>('/api/promotions', params);
  }

  async getPromotionById(
    promotionId: number,
    type?: string,
    searched?: boolean,
  ) {
    const params: Record<string, string> = {};
    if (type) {
      params.type = type;
    }
    if (searched) {
      params.searched = '1';
    }
    return this.get<any>(`/api/promotions/${promotionId}`, params);
  }

  // Customer Groups API
  async getCustomerGroups(
    page: number = 1,
    limit: number = 50,
    filters?: { name?: string },
  ) {
    const params: Record<string, string | number> = { page, limit };
    if (filters?.name) {
      params.name = filters.name;
    }

    const hasFilters = Boolean(filters?.name);
    const response = await this.client.get('/api/customer-groups', {
      params,
      timeout: hasFilters ? 120000 : undefined,
    });
    return response.data;
  }

  async getCustomerGroupById(groupId: number) {
    return this.get<any>(`/api/customer-groups/${groupId}`);
  }

  async getCustomerGroupCounts(groupIds: number[]) {
    const response = await this.client.get('/api/customer-groups/counts', {
      params: { groupIds: groupIds.join(',') },
      timeout: 120000,
    });
    return response.data;
  }

  async getCustomersByGroupId(groupId: number, page: number = 1, limit: number = 50) {
    return this.get<any[]>(`/api/customer-groups/${groupId}/customers`, { page, limit });
  }

  // Customers API
  async getCustomers(
    page: number = 1,
    limit: number = 50,
    searchFilters?: { email?: string }
  ) {
    const params: Record<string, any> = { page, limit };
    if (searchFilters?.email) { params.email = searchFilters.email; }
    return this.get<any[]>('/api/customers', params);
  }

  async getCustomerById(customerId: number, emailSearch?: string) {
    const params: Record<string, string> = {};
    if (emailSearch?.trim()) {
      params.emailSearch = emailSearch.trim();
    }
    return this.get<any>(`/api/customers/${customerId}`, params);
  }

  async getAuthMe() {
    return this.get<AuthMePersona>('/api/auth/me');
  }

  // Dashboard / Warehouse API
  async getDailyPurchases(startDate?: string, endDate?: string) {
    const params: Record<string, string> = {};
    if (startDate) { params.startDate = startDate; }
    if (endDate)   { params.endDate   = endDate; }
    const response = await this.client.get('/api/dashboard/daily-purchases', { params });
    return response.data;
  }

  async getAmbrAnalysis() {
    const response = await this.client.get('/api/dashboard/ambr-analysis');
    return response.data;
  }

  async getProductAnalysis(fiscalYear?: FiscalYear) {
    const params: Record<string, string> = {};
    if (fiscalYear) { params.fiscalYear = fiscalYear; }
    const response = await this.client.get('/api/dashboard/product-analysis', { params });
    return response.data;
  }

  async getProductOrders(sku: string, fiscalYear?: FiscalYear) {
    const params: Record<string, string> = {};
    if (fiscalYear) { params.fiscalYear = fiscalYear; }
    const response = await this.client.get(
      `/api/dashboard/product-orders/${encodeURIComponent(sku)}`,
      { params },
    );
    return response.data;
  }

  async getMonitoring(hours: number = 24) {
    const response = await this.client.get('/api/monitoring', { params: { hours } });
    return response.data;
  }

  async getMonitoringUserActivity(email: string, hours: number = 24): Promise<UserActivityDetail> {
    const response = await this.client.get<UserActivityDetail>('/api/monitoring/user-activity', {
      params: { email, hours },
    });
    return response.data;
  }

  async postUserEvent(payload: UserEventPayload): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean }>(
      '/api/monitoring/user-events',
      payload,
    );
    return response.data;
  }

  async getBcPermissionsAuditUsers(): Promise<BcPermissionsAuditResponse> {
    const response = await this.client.get<BcPermissionsAuditResponse>(
      '/api/bc-permissions-audit/users',
    );
    return response.data;
  }

  getBcPermissionsAuditExportUrl(): string {
    return `${getApiBaseUrl()}/api/bc-permissions-audit/export.csv`;
  }

  getBcPermissionsAuditMatrixExportUrl(): string {
    return `${getApiBaseUrl()}/api/bc-permissions-audit/export-matrix.csv`;
  }

  async getBcPermissionTemplates(): Promise<{ templates: PermissionTemplateSummary[] }> {
    const response = await this.client.get<{ templates: PermissionTemplateSummary[] }>(
      '/api/bc-permissions-audit/templates',
    );
    return response.data;
  }

  async getBcPermissionTemplate(id: string): Promise<PermissionTemplate> {
    const response = await this.client.get<PermissionTemplate>(
      `/api/bc-permissions-audit/templates/${encodeURIComponent(id)}`,
    );
    return response.data;
  }

  async downloadBcPermissionsAuditExport(kind: 'detail' | 'matrix'): Promise<void> {
    const path = kind === 'matrix'
      ? '/api/bc-permissions-audit/export-matrix.csv'
      : '/api/bc-permissions-audit/export.csv';
    const response = await this.client.get(path, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="([^"]+)"/);
    const filename = filenameMatch?.[1]
      ?? (kind === 'matrix' ? 'bc-permissions-matrix.csv' : 'bc-permissions-detail.csv');
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}

export const apiClient = new ApiClient();
